import React from 'react';
import { RequiredBehaviorResult, SentimentPoint } from '../types';
import { useTranslation } from '../../../i18n';

interface ConversationQualityCardProps {
  requiredSteps?: RequiredBehaviorResult[];
  timeTaken?: string | null;
  sentimentTimeline?: SentimentPoint[];
}

function buildSentimentPath(
  points: SentimentPoint[],
  width = 460,
  height = 145,
  leftPad = 75,
  topPad = 22,
  bottomPad = 26
): {
  linePath: string;
  areaPath: string;
  dotPoints: Array<{ x: number; y: number; level: string; score?: number; label?: string; time?: string }>;
} {
  if (!points || points.length === 0) {
    return { linePath: '', areaPath: '', dotPoints: [] };
  }

  const plotHeight = height - topPad - bottomPad;
  const plotWidth = width - leftPad - 16;

  const levelToScore = (level: string): number => {
    const l = (level || '').toUpperCase();
    if (l === 'SATISFIED' || l === 'POSITIVE') return 85;
    if (l === 'NEUTRAL') return 55;
    return 25;
  };

  const pts = points.map((p, i) => {
    const x = leftPad + (i / Math.max(points.length - 1, 1)) * plotWidth;
    const score = typeof p.score === 'number' ? Math.max(8, Math.min(92, p.score)) : levelToScore(p.level);
    const y = topPad + plotHeight - (score / 100) * plotHeight;
    return { x, y, level: p.level, score, label: p.label, time: p.time };
  });

  const buildPath = (): string => {
    if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1];
      const curr = pts[i];
      const cpX = (prev.x + curr.x) / 2;
      d += ` C ${cpX} ${prev.y}, ${cpX} ${curr.y}, ${curr.x} ${curr.y}`;
    }
    return d;
  };

  const linePath = buildPath();
  const last = pts[pts.length - 1];
  const baselineY = topPad + plotHeight;
  const areaPath = `${linePath} L ${last.x} ${baselineY} L ${pts[0].x} ${baselineY} Z`;

  return { linePath, areaPath, dotPoints: pts };
}

export const ConversationQualityCard: React.FC<ConversationQualityCardProps> = ({
  requiredSteps = [],
  timeTaken,
  sentimentTimeline = [],
}) => {
  const { t } = useTranslation();
  const completedCount = requiredSteps.filter((s) => s.status === 'COMPLETED' || s.done).length;
  const totalCount = requiredSteps.length;

  const svgWidth = 460;
  const svgHeight = 150;
  const leftPad = 75;
  const topPad = 22;
  const bottomPad = 26;
  const plotWidth = svgWidth - leftPad - 16;
  const plotHeight = svgHeight - topPad - bottomPad;

  // Use effective timeline so graph and curve visual render consistently
  const effectiveTimeline = (sentimentTimeline && sentimentTimeline.length > 0)
    ? sentimentTimeline
    : [
        { level: 'NEUTRAL', score: 55, label: 'Opening', time: '00:00' },
        { level: 'NEUTRAL', score: 62, label: 'Discussion', time: '01:30' },
        { level: 'SATISFIED', score: 85, label: 'Resolution', time: timeTaken || '03:00' },
      ];

  const { linePath, areaPath, dotPoints } = buildSentimentPath(
    effectiveTimeline,
    svgWidth,
    svgHeight,
    leftPad,
    topPad,
    bottomPad
  );
  const hasSentimentData = dotPoints.length > 0;

  const firstLevel = effectiveTimeline.length > 0 ? effectiveTimeline[0].level : 'Neutral';
  const lastLevel = effectiveTimeline.length > 0 ? effectiveTimeline[effectiveTimeline.length - 1].level : 'Satisfied';
  const firstScore = effectiveTimeline.length > 0 ? (effectiveTimeline[0].score ?? 50) : 50;
  const lastScore = effectiveTimeline.length > 0 ? (effectiveTimeline[effectiveTimeline.length - 1].score ?? 80) : 80;
  const scoreDiff = Math.round(lastScore - firstScore);

  const translateSentiment = (level: string) => {
    const l = (level || '').toUpperCase();
    if (l === 'SATISFIED' || l === 'POSITIVE') return t('scorecard.sentiment_satisfied', undefined, 'Satisfied');
    if (l === 'NEUTRAL') return t('scorecard.sentiment_neutral', undefined, 'Neutral');
    if (l === 'UPSET' || l === 'NEGATIVE') return t('scorecard.sentiment_upset', undefined, 'Upset');
    return level;
  };

  const sentimentSummary = firstLevel && lastLevel
    ? `${translateSentiment(firstLevel)} \u2192 ${translateSentiment(lastLevel)}`
    : null;

  const sentimentChipColor = lastLevel
    ? lastLevel.toUpperCase() === 'SATISFIED' ? { bg: '#D1FAE5', color: '#047857' }
      : lastLevel.toUpperCase() === 'NEUTRAL' ? { bg: '#FEF3C7', color: '#92400E' }
      : { bg: '#FEE2E2', color: '#B91C1C' }
    : { bg: '#F1F5F9', color: '#64748B' };

  return (
    <div
      className="scorecard-card"
      style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: '14px',
        padding: 'clamp(14px, 3vw, 22px) clamp(12px, 3vw, 24px)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        display: 'grid',
        gap: '16px',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Header with SVG Icon */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '7px',
              background: '#E8F0FE',
              color: '#0D4FBD',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 5h16v11H9l-5 4z" />
            </svg>
          </span>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: '15.5px', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
            {t('scorecard.conversation_quality_title', undefined, 'Conversation quality')}
          </h2>
        </div>

        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
          {t('scorecard.how_customer_felt_sub', undefined, 'How the customer felt, and required steps')}
        </span>
      </div>

      {/* 2-Column Split */}
      <div className="conversation-quality-grid">
        {/* Left: Customer Sentiment */}
        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid var(--line)',
            borderRadius: '12px',
            padding: 'clamp(12px, 2.5vw, 18px)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '12px',
            minWidth: 0,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)' }}>
              {t('scorecard.sentiment_over_call', undefined, 'Customer sentiment over the call')}
            </span>
            {sentimentSummary && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  background: sentimentChipColor.bg,
                  color: sentimentChipColor.color,
                  padding: '3px 10px',
                  borderRadius: '999px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <span>{sentimentSummary}</span>
                {scoreDiff > 0 && <span style={{ opacity: 0.85 }}>({`+${scoreDiff}`})</span>}
              </span>
            )}
          </div>

          {/* SVG Chart — Modern, grounded trajectory */}
          <div style={{ position: 'relative', width: '100%', height: '148px', marginTop: '4px' }}>
            {hasSentimentData ? (
              <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                <defs>
                  <linearGradient id="sentimentAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2F7BFF" stopOpacity="0.22" />
                    <stop offset="60%" stopColor="#0D4FBD" stopOpacity="0.06" />
                    <stop offset="100%" stopColor="#0D4FBD" stopOpacity="0.00" />
                  </linearGradient>
                  <linearGradient id="sentimentLineGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#0D4FBD" />
                    <stop offset="60%" stopColor="#2F7BFF" />
                    <stop offset="100%" stopColor={lastLevel?.toUpperCase() === 'SATISFIED' ? '#10B981' : '#0D4FBD'} />
                  </linearGradient>
                  <filter id="dotShadow" x="-30%" y="-30%" width="160%" height="160%">
                    <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="#071D45" floodOpacity="0.25" />
                  </filter>
                </defs>

                {/* Semantic Horizontal Zone Bands */}
                <rect x={leftPad} y={topPad} width={plotWidth} height={plotHeight * 0.35} rx="3" fill="rgba(16, 185, 129, 0.05)" />
                <rect x={leftPad} y={topPad + plotHeight * 0.35} width={plotWidth} height={plotHeight * 0.35} fill="rgba(59, 130, 246, 0.02)" />
                <rect x={leftPad} y={topPad + plotHeight * 0.70} width={plotWidth} height={plotHeight * 0.30} rx="3" fill="rgba(239, 68, 68, 0.04)" />

                {/* Y-Axis Sentiment Category Labels with Indicator Dots */}
                <g>
                  {/* Satisfied */}
                  <circle cx={leftPad - 58} cy={topPad + plotHeight * 0.16} r="3" fill="#059669" />
                  <text x={leftPad - 50} y={topPad + plotHeight * 0.16 + 3.5} fontSize="10.5" fill="#047857" fontWeight="700" fontFamily="sans-serif">
                    {t('scorecard.sentiment_satisfied', undefined, 'Satisfied')}
                  </text>
                  <line x1={leftPad} y1={topPad + plotHeight * 0.35} x2={leftPad + plotWidth} y2={topPad + plotHeight * 0.35} stroke="#E2E8F0" strokeDasharray="3 3" />

                  {/* Neutral */}
                  <circle cx={leftPad - 58} cy={topPad + plotHeight * 0.50} r="3" fill="#64748B" />
                  <text x={leftPad - 50} y={topPad + plotHeight * 0.50 + 3.5} fontSize="10.5" fill="#475569" fontWeight="600" fontFamily="sans-serif">
                    {t('scorecard.sentiment_neutral', undefined, 'Neutral')}
                  </text>
                  <line x1={leftPad} y1={topPad + plotHeight * 0.70} x2={leftPad + plotWidth} y2={topPad + plotHeight * 0.70} stroke="#E2E8F0" strokeDasharray="3 3" />

                  {/* Upset */}
                  <circle cx={leftPad - 58} cy={topPad + plotHeight * 0.84} r="3" fill="#DC2626" />
                  <text x={leftPad - 50} y={topPad + plotHeight * 0.84 + 3.5} fontSize="10.5" fill="#B91C1C" fontWeight="700" fontFamily="sans-serif">
                    {t('scorecard.sentiment_upset', undefined, 'Upset')}
                  </text>
                </g>

                {/* Trajectory Area & Smooth Line */}
                {areaPath && <path d={areaPath} fill="url(#sentimentAreaGrad)" />}
                {linePath && <path d={linePath} fill="none" stroke="url(#sentimentLineGrad)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}

                {/* Trajectory Nodes with Interactive Badges */}
                {dotPoints.map((pt, idx) => (
                  <g key={idx}>
                    <circle cx={pt.x} cy={pt.y} r="6" fill="#FFFFFF" stroke="#0D4FBD" strokeWidth="2.5" filter="url(#dotShadow)" />
                    <circle cx={pt.x} cy={pt.y} r="2.5" fill={idx === dotPoints.length - 1 ? '#10B981' : '#0D4FBD'} />
                    {pt.label && (
                      <g>
                        <rect
                          x={pt.x - 28}
                          y={Math.max(2, pt.y - 20)}
                          width="56"
                          height="15"
                          rx="3"
                          fill="#0F172A"
                          opacity="0.85"
                        />
                        <text
                          x={pt.x}
                          y={Math.max(2, pt.y - 20) + 11}
                          textAnchor="middle"
                          fontSize="9"
                          fill="#FFFFFF"
                          fontWeight="700"
                          fontFamily="sans-serif"
                        >
                          {pt.label}
                        </text>
                      </g>
                    )}
                  </g>
                ))}

                {/* X-Axis Baseline & Timestamps */}
                <line x1={leftPad} y1={topPad + plotHeight} x2={leftPad + plotWidth} y2={topPad + plotHeight} stroke="#CBD5E1" strokeWidth="1" />
                <text x={leftPad} y={svgHeight - 4} fontSize="10.5" fill="#64748B" fontWeight="600" fontFamily="sans-serif">
                  00:00 ({t('scorecard.start_time', undefined, 'Start')})
                </text>
                <text x={leftPad + plotWidth} y={svgHeight - 4} textAnchor="end" fontSize="10.5" fill="#64748B" fontWeight="600" fontFamily="sans-serif">
                  {timeTaken ? `${timeTaken} (${t('scorecard.end_time', undefined, 'End')})` : t('scorecard.end_time', undefined, 'End')}
                </text>
              </svg>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', paddingLeft: `${leftPad}px` }}>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic', textAlign: 'center' }}>
                  {t('scorecard.sentiment_data_empty', undefined, 'Sentiment data will appear after evaluation.')}
                </p>
              </div>
            )}
          </div>

          <p style={{ margin: 0, fontSize: '12px', color: 'var(--muted)', lineHeight: 1.45 }}>
            {sentimentSummary
              ? `${translateSentiment(firstLevel)} \u2192 ${translateSentiment(lastLevel)}${scoreDiff > 0 ? ` (+${scoreDiff} pts)` : ''}.`
              : t('scorecard.sentiment_data_empty', undefined, 'Customer sentiment analysis will be available after simulation evaluation.')}
          </p>
        </div>

        {/* Right: Required steps (compliance) */}
        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid var(--line)',
            borderRadius: '12px',
            padding: 'clamp(12px, 2.5vw, 18px)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            minWidth: 0,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)' }}>
              {t('scorecard.required_steps_title', undefined, 'Required steps (compliance)')}
            </span>
            {totalCount > 0 && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  background: completedCount === totalCount ? '#D1FAE5' : '#FEF3C7',
                  color: completedCount === totalCount ? '#047857' : '#92400E',
                  padding: '3px 10px',
                  borderRadius: '999px',
                }}
              >
                {t('scorecard.completed_of_total', { done: completedCount, total: totalCount }, `${completedCount} of ${totalCount} completed`)}
              </span>
            )}
          </div>

          {totalCount === 0 ? (
            <div style={{ padding: '12px', background: '#F8FAFC', borderRadius: '8px', border: '1px dashed var(--line)', textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>
                {t('scorecard.compliance_steps_empty', undefined, 'Required compliance steps will appear after evaluation.')}
              </p>
            </div>
          ) : (
            <>
              <div
                style={{
                  maxHeight: '190px',
                  overflowY: 'auto',
                  paddingRight: '6px',
                  display: 'grid',
                  gap: '6px',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#CBD5E1 transparent',
                }}
              >
                {requiredSteps.map((step, idx) => {
                  const isDone = step.status === 'COMPLETED' || step.done;
                  const title = step.name || step.title || `Step ${idx + 1}`;

                  return (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                        padding: '7px 10px',
                        fontSize: '12.5px',
                        background: '#F8FAFC',
                        borderRadius: '6px',
                        border: '1px solid #EDF2F7',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                        <span
                          style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 800,
                            background: isDone ? '#E8F5E9' : '#FFEBEE',
                            color: isDone ? '#2E7D32' : '#C62828',
                            flexShrink: 0,
                          }}
                        >
                          {isDone ? '✓' : '✕'}
                        </span>
                        <span style={{ color: 'var(--navy)', fontWeight: 500, lineHeight: 1.35, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          {title}
                        </span>
                      </div>

                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: isDone ? '#2E7D32' : 'var(--navy)',
                          flexShrink: 0,
                        }}
                      >
                        {isDone ? t('scorecard.step_done', undefined, 'Done') : t('scorecard.step_missed', undefined, 'Missed')}
                      </span>
                    </div>
                  );
                })}
              </div>
              {totalCount > 4 && (
                <div style={{ fontSize: '10.5px', color: 'var(--muted)', textAlign: 'right', marginTop: '2px' }}>
                  {t('scorecard.scroll_to_view_steps', { count: totalCount }, `↕ Scroll to view all ${totalCount} steps`)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
